import React from "react";
import PageShell, { SectionHeader } from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { MEDIA, CONTACT_EMAIL, SPEAKING, COACHING, OBSERVATIONS } from "@/data/content";
import { Link } from "react-router-dom";

const PRESS_BIO_SHORT =
  "Quentin Young is a Canadian entrepreneur, founder, operator, brand strategist, and creative leader. He owns and operates Kostaja Ltd. and a slate of ventures (Operating Class, LotWise Auto, LotWise Home, Agent Marketing) and is the creative mind behind the brand and websites of Murray Mortgages, Bristol Auto Performance, and others.";

const PRESS_BIO_LONG =
  "Quentin Young writes and works at the intersection of human nature, culture, systems, leadership, brand, and craft. His public archive documents the conditions that shape individual and collective potential: how organizations behave, how leaders create environments, how perception is built through detail, and how execution becomes evidence. He speaks and coaches privately with people building, leading, selling, positioning, and becoming something more serious.";

export default function Media() {
  return (
    <PageShell
      testId="page-media"
      title="Media | Quentin Young"
      description="Media, appearances, mentions, interviews, and press resources for Quentin Young."
      ogImage="/og-media.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Media", url: "/media" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <p className="eyebrow-gold mb-5">Media</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          Third-party validation will appear here as it earns the page.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          Interviews, podcasts, articles, features, and speaking. No invented coverage. No empty theatre. What lives here is only what actually exists.
        </p>
      </section>

      {/* Topics Quentin speaks and writes on */}
      <section className="bg-ink-base border-t border-ink-line py-16 md:py-24" data-testid="media-topics">
        <div className="container-edge">
          <SectionHeader
            eyebrow="For journalists, producers, and interviewers"
            title="Topics on the record."
            lead="Any of the below can be shaped into an interview, a panel appearance, a written contribution, or a keynote. Every topic is grounded in the observations and work published on this site."
          />
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {SPEAKING.themes.map((t) => (
              <li key={t.number} className="bg-ink-base p-8 md:p-10" data-testid={`media-topic-${t.number}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[11px] text-gold">{t.number}</span>
                  <h3 className="font-serif text-xl text-paper-primary leading-tight text-balance">{t.title}</h3>
                </div>
                <p className="mt-4 text-paper-secondary text-sm leading-relaxed text-pretty">{t.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-10 font-serif italic text-paper-tertiary text-lg max-w-2xl">
            Related coaching lanes on the same territory: {COACHING.lanes.map((l) => l.name).join(", ")}.
          </p>
        </div>
      </section>

      {/* Bio and press materials */}
      <section className="bg-ink-base border-t border-ink-line py-16 md:py-24" data-testid="media-bio">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Bio, headshots, and materials" title="Approved copy and assets." />
          </div>
          <div className="lg:col-span-8 space-y-8">
            <div>
              <p className="eyebrow-gold mb-3">Short bio</p>
              <p className="text-paper-secondary text-base leading-relaxed text-pretty" data-testid="media-bio-short">
                {PRESS_BIO_SHORT}
              </p>
            </div>
            <div>
              <p className="eyebrow-gold mb-3">Long bio</p>
              <p className="text-paper-secondary text-base leading-relaxed text-pretty" data-testid="media-bio-long">
                {PRESS_BIO_LONG}
              </p>
            </div>
            <div>
              <p className="eyebrow-gold mb-3">Headshots and additional assets</p>
              <p className="text-paper-tertiary text-sm leading-relaxed max-w-xl">
                A press kit with high-resolution headshots, alternate crops, and approved logo files can be requested directly. Include the outlet and intended use so the appropriate variants are shared.
              </p>
              <Link to="/contact?intent=media" data-testid="media-request-assets" className="btn-arrow mt-5 inline-flex">
                <span>Request bio and assets</span>
                <span className="arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent thinking (freshness + internal linking) */}
      <section className="bg-ink-base border-t border-ink-line py-16 md:py-24" data-testid="media-recent-thinking">
        <div className="container-edge">
          <div className="flex items-baseline justify-between mb-8">
            <p className="eyebrow">Recent thinking to draw from</p>
            <Link to="/thinking" className="btn-arrow" data-testid="media-view-all-thinking"><span>Full archive</span><span className="arrow">→</span></Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line border border-ink-line">
            {OBSERVATIONS.slice(0, 3).map((o) => (
              <li key={o.slug} className="bg-ink-base">
                <Link to={`/thinking/${o.slug}`} data-testid={`media-observation-${o.slug}`} className="block p-8 group">
                  <p className="eyebrow mb-3">Observation</p>
                  <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors leading-snug text-balance">
                    {o.headline}
                  </p>
                  {o.body && (
                    <p className="mt-3 text-paper-tertiary text-sm leading-relaxed line-clamp-3">{o.body}</p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Coverage list or empty-state */}
      <section className="bg-ink-base border-t border-ink-line py-16">
        <div className="container-edge">
          <p className="eyebrow mb-8">Coverage on record</p>
          {MEDIA.length === 0 ? (
            <div className="border border-ink-line bg-ink-surface/40 p-12 md:p-16" data-testid="media-empty-state">
              <p className="eyebrow-gold mb-5">Currently unpublished</p>
              <h2 className="display-serif text-2xl md:text-4xl text-paper-primary text-balance max-w-2xl">
                Nothing on the record yet. Designed to remain understated until it is.
              </h2>
              <p className="mt-6 max-w-xl text-paper-tertiary text-sm md:text-base">
                Working journalists, podcast hosts, and producers are welcome to reach out for thoughtful editorial. The topics and materials above indicate the substance available for record.
              </p>
              <Link to="/contact?intent=media" className="btn-quiet mt-8 inline-flex" data-testid="media-contact-link">
                <span>Press inquiry</span>
                <span>→</span>
              </Link>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                Or write directly · <a href={`mailto:${CONTACT_EMAIL}?subject=Media%20Inquiry`} className="hover:text-gold-warm transition-colors">{CONTACT_EMAIL}</a>
              </p>
            </div>
          ) : (
            <ul className="space-y-px bg-ink-line">
              {MEDIA.map((m, i) => (
                <li key={i} className="bg-ink-base p-8 md:p-10" data-testid={`media-entry-${i}`}>
                  <div className="grid grid-cols-12 gap-6 items-baseline">
                    <p className="col-span-2 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">{m.date}</p>
                    <div className="col-span-10">
                      <h3 className="font-serif text-2xl text-paper-primary">{m.title}</h3>
                      <p className="mt-2 text-paper-tertiary text-sm">{m.outlet} · {m.type}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="If media brought you here, thinking is where the work is."
        rooms={[
          { label: "Read the field notes", to: "/thinking", hint: "topic" },
          { label: "Invite Quentin to speak", to: "/speaking", hint: "speaking" },
          { label: "Reach out for media", to: "/contact?intent=media", hint: "conversation" },
        ]}
        testId="media-next"
      />
    </PageShell>
  );
}
